import { motion, useMotionValue, useReducedMotion, useSpring, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { EASE, Magnetic } from "./Reveal";
import { IMAGES, TRUST_ITEMS } from "../data/content";

const MaskedLine = ({ children, index, base = 0.2 }) => (
  <span className="block overflow-hidden pb-[0.1em] -mb-[0.1em]">
    <motion.span
      className="block will-change-transform"
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.2, delay: base + 0.35 + index * 0.16, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const Seal = () => (
  <div className="relative w-28 h-28 md:w-36 md:h-36" data-testid="hero-seal">
    <motion.svg
      viewBox="0 0 100 100"
      className="w-full h-full"
      animate={{ rotate: 360 }}
      transition={{ duration: 28, repeat: Infinity, ease: "linear" }}
    >
      <defs>
        <path id="seal-circle" d="M50,50 m-37,0 a37,37 0 1,1 74,0 a37,37 0 1,1 -74,0" />
      </defs>
      <text className="fill-white/80" style={{ fontSize: "8px", letterSpacing: "2.4px", fontFamily: "'JetBrains Mono', monospace", fontWeight: 600 }}>
        <textPath href="#seal-circle">MANFRED BERGLAR · MALERMEISTER · SEIT 1925 ·</textPath>
      </text>
    </motion.svg>
    <span className="absolute inset-0 flex items-center justify-center">
      <span className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-white border border-white/40 flex items-center justify-center shadow-lg">
        <img src="/images/wappen.png" alt="" className="h-10 md:h-12 w-auto" />
      </span>
    </span>
  </div>
);

export const Hero = () => {
  const reduced = useReducedMotion();
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const px = useSpring(useTransform(mx, [0, 1], [-12, 12]), { stiffness: 45, damping: 18 });
  const py = useSpring(useTransform(my, [0, 1], [-8, 8]), { stiffness: 45, damping: 18 });

  const onMove = (e) => {
    if (reduced || !window.matchMedia("(pointer: fine)").matches) return;
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width);
    my.set((e.clientY - r.top) / r.height);
  };

  return (
    <section
      id="start"
      onMouseMove={onMove}
      className="relative min-h-[100svh] flex flex-col justify-end bg-navy overflow-hidden"
      data-testid="hero-section"
    >
      <motion.div className="absolute inset-[-2%]" style={reduced ? undefined : { x: px, y: py }}>
        <motion.div
          className="absolute inset-0"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.6, ease: "easeOut" }}
        >
          <img
            src={IMAGES.hero}
            alt="Luftaufnahme von Olpe im goldenen Abendlicht – Altstadt mit Kirchturm, Biggesee und die bewaldeten Höhen des Südsauerlands, Heimat von Manfred Berglar, Malermeister"
            className={`w-full h-full object-cover ${reduced ? "" : "kenburns"}`}
            style={{ filter: "saturate(1.05)" }}
            fetchPriority="high"
            data-testid="hero-image"
          />
        </motion.div>
      </motion.div>
      <div className="absolute inset-0 bg-black/25 pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/10 to-transparent pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-black/10 to-transparent pointer-events-none" />
      <div className="absolute right-8 md:right-14 top-[112px] md:top-[140px] z-10 hidden sm:block">
        <motion.div initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.1, delay: 1.3, ease: EASE }}>
          <Seal />
        </motion.div>
      </div>
      <div className="container-x relative z-10 pb-36 md:pb-20 pt-[140px]">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.12, ease: EASE }}
        >
          <Link
            to="/karriere"
            data-testid="hero-hiring-badge"
            className="group inline-flex items-center gap-2.5 bg-white/10 backdrop-blur-md border border-white/25 rounded-full pl-3.5 pr-4 py-2 mb-6 transition-colors duration-300 hover:bg-white/20"
          >
            <span className="relative flex h-2 w-2" aria-hidden="true">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-herald opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-herald"></span>
            </span>
            <span className="font-mono text-[10px] md:text-[11px] uppercase tracking-[0.2em] font-bold text-white">
              Wir stellen ein
            </span>
            <ArrowUpRight size={13} className="text-herald transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </motion.div>
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.25, ease: EASE }}
          className="kicker-dark"
          data-testid="hero-kicker"
        >
          Malermeisterbetrieb · Olpe-Griesemert · seit 1925
        </motion.p>
        <h1
          data-testid="hero-headline"
          className="font-display text-white font-extrabold text-[clamp(2.6rem,6.8vw,5.6rem)] leading-[1.0] tracking-tighter text-balance mt-5 max-w-4xl"
        >
          <MaskedLine index={0}>Präzision in</MaskedLine>
          <MaskedLine index={1}>
            jeder <span className="font-fraktur text-herald">Dimension.</span>
          </MaskedLine>
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.85, ease: EASE }}
          className="max-w-xl text-base md:text-lg leading-relaxed text-white/80 mt-6"
          data-testid="hero-subline"
        >
          Malerarbeiten, Fassaden und Oberflächen für Privatkunden, Unternehmen und Kommunen –
          ausgeführt mit der Präzision eines Meisterbetriebs.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.0, ease: EASE }}
          className="flex flex-wrap gap-4 mt-9"
        >
          <Magnetic>
            <Link to="/kontakt" data-testid="hero-cta-primary" className="btn-light">
              Projekt anfragen <ArrowUpRight size={16} />
            </Link>
          </Magnetic>
          <Link to="/leistungen" data-testid="hero-cta-secondary" className="btn-outline-light">
            Leistungen ansehen
          </Link>
        </motion.div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.2 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-6 border-t border-white/20 mt-12 pt-6"
          data-testid="hero-stats"
        >
          {TRUST_ITEMS.map((s, i) => (
            <div key={s.value} data-testid={`hero-stat-${i}`} className="flex items-start gap-3">
              <span className="w-2 h-2 bg-herald rotate-45 mt-2 shrink-0" aria-hidden="true" />
              <div>
                <p className="font-mono text-xl md:text-2xl font-bold tracking-tight text-white tabular-nums">{s.value}</p>
                <p className="text-[11px] uppercase tracking-[0.18em] text-white/55 mt-1.5">{s.label}</p>
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
